/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import ko from "knockout";
import birdata from "../../api.js";
import _ from "underscore";
import SortableTableViewModel from "../shared/sortable-table.js";
import util from '../../util/util.js';
import moment from "dayjs";

// Wraps a function fn, and forwards all arguments to that function plus an additional 'isStale' argument
// This isStale argument is a function that returns true if the wrapped function was called
function preventStale(fn) {
	function wrapper() {
		var token = {};
		wrapper.token = token;

		function isStale() {
			return token !== wrapper.token;
		}

		var args = [].slice.call(arguments);
		args.unshift(isStale);
		fn.apply(this, args);
	}
	return wrapper;
}

function PermissionsViewModel(params) {
	var self = this;

	console.log(params);

	self.editPermissionViewModel = ko.observable(null);
	self.loading = ko.observable(false);

	var user = ko.observable(params.user);
	self.user = user;
	var parent = params.parent;
	var me = params.me; // observable

	var programs = ko.observable();
	birdata.lookup('program').then(programs);

	var regions = ko.observable();
	birdata.regions().then(regions);

	var species = ko.observable();
	birdata.speciesList().then(species);

	var areas = ko.observable();
	// birdata.areas().then(areas);

	// Include layer in area name for drop down menu
	Promise.all([
		birdata.areas(),
		birdata.areaLayers({ include_hidden: true })
	]).then(function([rawAreas, layers]) {
		areas(_.map(rawAreas, function(area) {
			var layer = _.findWhere(layers, {id: area.layer_id});
			return {
				id: area.id,
				title: area.name + " (" + layer.name + ")"
			};
		}));
	});

	self.areas = areas;

	self.title = ko.pureComputed(function() {
		var u = user();
		var title = u.full_name;
		if(user.raisers_edge_id) title += " (" + user.raisers_edge_id + ")";

		return title;
	});

	self.canEditAdministrator = ko.pureComputed(function() {
		return me() && me().is_administrator;
	});

	self.isAdministrator = ko.observable(user().is_administrator);

	self.isAdministrator.subscribe(function(isAdministrator) {
		if(isAdministrator != user().is_administrator) {
			if(!isAdministrator && me().id == user().id) {
				if(!window.confirm("Are you sure you wish to revoke your own administrator priviliges")) {
					self.isAdministrator(user().is_administrator);
					return;
				}
			}

			self.loading(true);
			birdata.updatePermission(user().id, {
				type: "global",
				role: "admin",
				action: isAdministrator ? "add" : "delete"
			}).then(function() {
				self.refresh();
			}).catch(function(e) {
				self.loading(false);
				self.isAdministrator(user().is_administrator);
				console.log(e);
				alert("Error updating permissions");
			});
		}
	});

	self.refresh = function() {
		self.loading(true);
		birdata.user(user().id, { full: true }).then(function(data) {
			user(data);
			self.isAdministrator(data.is_administrator);
			self.loading(false);
		});
	};
	self.refresh();

	function canEditProgram(programCode) {
		if(!me()) return false;
		if(me().is_administrator) return true;

		return _.chain(me().permissions)
			.where({ role: "admin", type: "program", program: programCode})
			.filter(function(p) { return !p.area_id; })
			.value()
			.length > 0;
	}

	function canEditRegion(regionId) {
		return !!(me() && me().is_administrator || _.findWhere(me().permissions, { role: "admin", type: "region", region_id: regionId }));
	}

	function canEditSpecies(speciesId) {
		return !!(me() && me().is_administrator || _.findWhere(me().permissions, { role: "admin", type: "species", region_id: speciesId }));
	}

	self.selectablePrograms = ko.pureComputed(function() {
		if(me() && programs()) {
			return _.filter(programs(), function(program) {
				return canEditProgram(program.code);
			});
		} else {
			return [];
		}
	});

	self.selectableRegions = ko.pureComputed(function() {
		if(me() && regions()) {
			return _.filter(regions(), function(region) {
				return canEditRegion(region.id);
			});
		} else {
			return [];
		}
	});

	self.selectableSpecies = ko.pureComputed(function() {
		if(me() && species()) {
			return _.filter(species(), function(sp) {
				return canEditSpecies(sp.id);
			});
		} else {
			return [];
		}
	});

	self.selectableTypes = ko.pureComputed(function() {
		var types = [];
		if(self.selectablePrograms().length > 0) {
			types.push({
				name: 'Program Admin',
				code: 'program_admin',
			});
			types.push({
				name: 'Program Vetter',
				code: 'program_vetter',
			});
			types.push({
				name: 'Program Participant',
				code: 'program_participant',
			});
			types.push({
				name: 'Private Site User',
				code: 'program_private_site_user',
			});
		}
		if(self.selectableRegions().length > 0) {
			types.push({
				name: 'Regional Group Admin',
				code: 'region_admin'
			});
			types.push({
				name: 'Regional Group Vetter',
				code: 'region_vetter'
			});
		}
		if(self.selectableSpecies().length > 0) {
			types.push({
				name: 'Species Admin',
				code: 'species_admin'
			});
			types.push({
				name: 'Species Vetter',
				code: 'species_vetter'
			});
		}
		return types;
	});

	self.done = function() {
		parent.permissionsViewModel(null);
	};

	self.remove = function(p) {
		self.loading(true);
		birdata.updatePermission(user().id, _.extend({ action: 'delete'}, p.json)).then(function() {
			self.refresh();
		}).catch(function(e) {
			self.loading(false);
			console.log(e);
			alert('There was a problem updating permissions');
		});
	};

	self.addPermission = function() {
		self.editPermissionViewModel(new PermissionViewModel({ parent: this }));
	};

	self.permissions = ko.pureComputed(function() {
		if(!user()) {
			return [];
		} else {
			return _.flatten(_.map(user().permissions, function(p) {
				let editable;
				if(p.type == "program") {
					editable = canEditProgram(p.program);
				} else if(p.type == "region") {
					editable = canEditRegion(p.region_id);
				} else if(p.type == "species") {
					editable = canEditSpecies(p.species_id);
				} else {
					return [];
				}

				return {
					name: p.role_description,
					resource: p.resource_description,
					editable,
					json: p
				};
			}));
		}
	});
}

function PermissionViewModel(params) {
	var self = this;

	self.type = ko.observable(params.type);
	self.program = ko.observable(params.program);
	self.region = ko.observable(params.region);
	self.species = ko.observable(params.species);
	self.area = ko.observable(params.area);

	self.parent = params.parent;
	self.saving = ko.observable(false);

	function permissionJson() {
		var typeRole = self.type().code.split('_');
		var p ={
			type: typeRole[0],
			role: typeRole.slice(1).join('_')
		};
		if(p.type == 'program' && self.program()) {
			p.program = self.program().code;
			if(self.area()) {
				p.area_id = self.area().id;
			}
		}
		if(p.type == 'region' && self.region()) {
			p.region_id = self.region().id;
		}
		if(p.type == 'species' && self.species()) {
			p.species_id = self.species().id;
		}

		return p;
	}

	self.cancel = function() {
		params.parent.editPermissionViewModel(null);
	};

	self.save = function() {
		self.saving(true);
		birdata.updatePermission(params.parent.user().id, permissionJson()).then(function(){
			params.parent.refresh();
			params.parent.editPermissionViewModel(null);
		}).catch(function(e) {
			console.log('Failed to update permissions');
			console.log(e);
			alert('There was a problem updating permissions');
		});
	};
}

function ManageUsersViewModel(params) {
	var self = this;

	self.searchHasFocus = ko.observable(true); // Immediately focus search field

	self.searchText = ko.observable('').extend({ rateLimit: 400 });
	self.users = ko.observable([]);
	self.usersLoaded = ko.observable(true);

	self.permissionsViewModel = ko.observable(null);

	var updateUsers = preventStale(function(isStale, text) {
		if(text != '') {
			self.usersLoaded(false);
			birdata.users({q: text, limit: 100, full: true }).then(function(users) {
				if(!isStale()) {
					self.users(users);
					self.usersLoaded(true);
				} else {
					console.log("Ignoring stale response");
				}
			});
		} else {
			self.users([]);
		}
	});

	self.searchText.subscribe(updateUsers);


	self.refresh = function() {
		updateUsers(self.searchText());
	}

	self.userTable = new SortableTableViewModel([
		{
			name: 'ID/Linked to SF',
			value: function(user) { return user.id + " " + (user.is_external ? 'Linked to SF' : ''); },
			html: function(user) { return user.id + "<br>" + (user.is_external ? 'Linked to SF' : ''); },
			id: 'id'
		}, {
			name: 'Name',
			value: function(user) { return user.full_name; },
			html: function(user) { return this.value(user); },
			id: 'name'
		}, {
			name: 'Email',
			value: function(user) { return user.email; },
			html: function(user) { return this.value(user); },
			id: 'email'
		}, {
			name: 'Surveys',
			value: function(user) { return user.survey_count; },
			html: function(user) { return util.formatNumber(this.value(user)); },
			id: 'survey_count'
		}, {
			name: 'Last survey date',
			value: function(user) { return user.last_survey_date; },
			html: function(user) { return user.last_survey_date ? moment(user.last_survey_date).format("DD/MM/YYYY") : ''; },
			id: 'last_survey_date'
		}, {
			name: 'Actions',
			value: function() { return ''; },
			formatted: function() { return ''; },
			id: 'actions',
			custom: true,
			sortable: false
		}
	]);

	// TODO: maybe this sorting logic should be refactored into SortableTableViewModel
	self.sortedUsers = ko.pureComputed(function() {
		var users = [ ...self.users() ];

		// Sort surveys according to table sort criteria
		_.each(self.userTable.sortDescriptors(), function(descriptor) {
			if(!descriptor.ascending) users.reverse();
			var column = self.userTable.column(descriptor.column);
			users = _.sortBy(users, column.params.value);
			if(!descriptor.ascending) users.reverse();
		});

		return users;
	});

	self.managePermissions = function(user) {
		self.permissionsViewModel(new PermissionsViewModel({
			user: user,
			parent: self,
			me: params.me
		}));
	};

	self.canDeleteUser = function(user) {
		// var me = ko.unwrap(params.me);
		// return me && me.is_administrator;
		return false;
	}

	self.deleteUser = function(user) {
		if(confirm("Are you sure you want to delete user " + user.id  + "(" + user.full_name + ", " + user.email + ")")) {
			birdata.deleteUser(user.id).then(function() {
				self.refresh();
			}).catch(function(e) {
				console.log(e);
				alert("Failed to delete user");
			});
		}
	}

	self.canMergeFromOther = function(user) {
		return user.is_external;
	}

	self.canMergeToOther = function(user) {
		var target = self.mergeState().target;
		return target && target.id != user.id;
	}

	// idle, selecting, confirming, saving, error
	self.mergeState = ko.observable({ state: 'idle'});
	self.mergeSelectionActive = ko.pureComputed(function() {
		return self.mergeState().state == 'selecting';
	});

	self.beginMergeUser = function(user) {
		self.mergeState({ state: 'selecting', target: user });
	}

	self.cancelMerge = function() {
		self.mergeState({ state: 'idle' });
	}

	self.selectMergeSource = function(user) {
		var target = self.mergeState().target;
		self.mergeState({ state: 'confirming', target: target, source: user });
	}

	self.performMerge = function() {
		var oldState = self.mergeState();
		self.mergeState({ state: 'saving'});
		birdata.mergeUser(oldState.source.id, oldState.target.id).then(function() {
			self.mergeState({ state: 'idle' });
			self.refresh();
		}).catch(function(e) {
			console.log('Failed to merge users')
			console.log(e);
			self.mergeState({ state: 'error'})
		});
	}
}

ko.components.register('manage-users', {
	viewModel: function(params) {
		return new ManageUsersViewModel(params);
	},
	template: require('../../../templates/admin/manage-users.html')
});
